import React, { useState } from "react";
import { Modal, Backdrop, Fade, CircularProgress } from "@material-ui/core";
import styled from "styled-components";
import NoticeService from "../../../../services/NoticeService";

const TrackingModal = ({ trackingModal, setTrackingModal }) => {
  const [expandedAWB, setExpandedAWB] = useState(null);
  const [trackingData, setTrackingData] = useState({});
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const awbList = [
    ...new Set(trackingModal?.trackingData || ["EA401250270IN"]),
  ];

  const filteredAwbList = awbList.filter((awb) =>
    awb.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredAwbList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentShipments = filteredAwbList.slice(startIndex, endIndex);

  const handleClose = () => {
    setTrackingModal({ state: false, trackingData: [] });
    setExpandedAWB(null);
    setSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedAWB(null);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  const extractAwbOnly = (text) => {
    if (!text) return null;

    if (!text.includes("(")) return text.trim();

    const match = text.match(/\(([^)]+)\)/);
    return match ? match[1].trim() : text.trim();
  };

  const fetchTrackingData = async (awb) => {
    const cleanAwb = extractAwbOnly(awb);
    setLoading((prev) => ({ ...prev, [cleanAwb]: true }));
    setErrors((prev) => ({ ...prev, [cleanAwb]: null }));

    try {
      const requestPayload = {
        awb_numbers: [cleanAwb],
      };

      const response = await NoticeService.getTrackingDetails(requestPayload);

      if (response?.success === true || response?.success === "true") {
        const shipmentData =
          response.shipment?.length > 0 ? response.shipment[0] : null;

        if (!shipmentData) throw new Error("No shipment data found");

        setTrackingData((prev) => ({
          ...prev,
          [cleanAwb]: shipmentData,
        }));
      } else {
        throw new Error(response?.message || "Failed to fetch tracking data");
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [cleanAwb]: error.message || "Failed to load tracking information",
      }));
    } finally {
      setLoading((prev) => ({ ...prev, [cleanAwb]: false }));
    }
  };

  const toggleAWB = async (awb) => {
    if (expandedAWB === awb) {
      setExpandedAWB(null);
    } else {
      setExpandedAWB(awb);
      if (!trackingData[awb]) {
        await fetchTrackingData(awb);
      }
    }
  };

  return (
    <Modal
      open={trackingModal?.state || false}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={trackingModal?.state || false}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Tracking Covers</ModalTitle>
            <CloseButton onClick={handleClose}>×</CloseButton>
          </ModalHeader>

          <ModalBody>
            {filteredAwbList.length > 10 && (
              <SearchContainer>
                <SearchIcon>{"🔍"}</SearchIcon>
                <SearchInput
                  type="text"
                  placeholder="Search by AWB number..."
                  value={searchTerm}
                  style={{ fontFamily: "openSans-SemiBold, sans-serif" }}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to first page on search
                    setExpandedAWB(null);
                  }}
                />
                {searchTerm && (
                  <ClearButton
                    onClick={() => {
                      setSearchTerm("");
                      setCurrentPage(1);
                    }}
                  >
                    ✕
                  </ClearButton>
                )}
              </SearchContainer>
            )}

            <AWBListContainer>
              {currentShipments.length === 0 ? (
                <EmptyState>No AWB numbers found</EmptyState>
              ) : (
                currentShipments.map((awbText) => {
                  const cleanAwb = extractAwbOnly(awbText);

                  return (
                    <AWBItem
                      key={cleanAwb}
                      onClick={() => toggleAWB(cleanAwb)}
                      isExpanded={expandedAWB === cleanAwb}
                    >
                      <ShipmentHeader>
                        <ShipmentInfo>
                          <AWBNumber>{awbText}</AWBNumber>{" "}
                          {trackingData[cleanAwb]?.status_date && (
                            <LastUpdated>
                              {formatDate(trackingData[cleanAwb].status_date)}
                            </LastUpdated>
                          )}
                        </ShipmentInfo>

                        <StatusContainer>
                          {trackingData[cleanAwb]?.status && (
                            <StatusBadge
                              colors={getStatusColor(
                                trackingData[cleanAwb].status,
                              )}
                            >
                              <StatusIcon>
                                {getStatusIcon(trackingData[cleanAwb].status)}
                              </StatusIcon>
                              {trackingData[cleanAwb].status}
                            </StatusBadge>
                          )}

                          <ExpandIcon isExpanded={expandedAWB === cleanAwb}>
                            ▼
                          </ExpandIcon>
                        </StatusContainer>
                      </ShipmentHeader>

                      {expandedAWB === cleanAwb && (
                        <TrackingDetails>
                          {loading[cleanAwb] && (
                            <LoadingContainer>
                              <CircularProgress
                                size={40}
                                style={{ color: "#00838c" }}
                              />
                            </LoadingContainer>
                          )}

                          {errors[cleanAwb] && (
                            <ErrorMessage>{errors[cleanAwb]}</ErrorMessage>
                          )}

                          {!loading[cleanAwb] &&
                            !errors[cleanAwb] &&
                            trackingData[cleanAwb] && (
                              <>
                                {trackingData[cleanAwb].event &&
                                trackingData[cleanAwb].event.length > 0 ? (
                                  <TimelineWrapper>
                                    <Timeline>
                                      {trackingData[cleanAwb].event.map(
                                        (scan, idx) => (
                                          <TimelineEvent
                                            key={idx}
                                            isLast={
                                              idx ===
                                              trackingData[cleanAwb].event
                                                .length -
                                                1
                                            }
                                          >
                                            <TimelineDot />
                                            <EventContent>
                                              <EventHeader>
                                                <EventLocation>
                                                  {typeof scan.location ===
                                                  "string"
                                                    ? scan.location
                                                    : scan.location?.name ||
                                                      "Unknown Location"}
                                                </EventLocation>
                                                <EventTime>
                                                  {formatDate(scan.status_date)}
                                                </EventTime>
                                              </EventHeader>
                                              <EventType>
                                                {scan.remarks || scan.status}
                                              </EventType>
                                            </EventContent>
                                          </TimelineEvent>
                                        ),
                                      )}
                                    </Timeline>
                                  </TimelineWrapper>
                                ) : (
                                  <NoDataMessage>
                                    No tracking events available
                                  </NoDataMessage>
                                )}
                              </>
                            )}
                        </TrackingDetails>
                      )}
                    </AWBItem>
                  );
                })
              )}
            </AWBListContainer>
          </ModalBody>

          {/* Pagination Section - Only show if more than 10 items */}
          {filteredAwbList.length > 10 && (
            <PaginationSection>
              <PaginationInfo>
                Showing {currentShipments.length === 0 ? 0 : startIndex + 1} to{" "}
                {Math.min(endIndex, filteredAwbList.length)} of{" "}
                {filteredAwbList.length}
              </PaginationInfo>

              <PaginationContainer>
                <PaginationButton
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </PaginationButton>

                {generatePageNumbers().map((page, idx) => (
                  <PageButton
                    key={idx}
                    type="button"
                    onClick={() =>
                      typeof page === "number" && handlePageChange(page)
                    }
                    active={page === currentPage}
                    disabled={page === "..."}
                  >
                    {page}
                  </PageButton>
                ))}

                <PaginationButton
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </PaginationButton>
              </PaginationContainer>
            </PaginationSection>
          )}
        </ModalContainer>
      </Fade>
    </Modal>
  );
};

export default TrackingModal;

const ModalContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  outline: none;
`;

const ModalHeader = styled.div`
  padding: 20px 24px;
  background: linear-gradient(135deg, #00838c 0%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: white;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  font-size: 24px;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: #999;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 12px 10px 40px;
  border: 2px solid #e0e0e0;
  background: #f8f9fa;
  border-radius: 8px;
  color: #293461;
  font-size: 14px;
  transition: all 0.3s;

  &::placeholder {
    color: #999;
  }

  &:focus {
    outline: none;
    background: white;
    box-shadow: 0 0 0 2px rgba(0, 131, 140, 0.1);
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: #e0e0e0;
  border: none;
  color: #666;
  font-size: 16px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #d0d0d0;
  }
`;

const AWBListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AWBItem = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(props) => (props.isExpanded ? "#f8f9fa" : "white")};
  border-color: ${(props) => (props.isExpanded ? "#00838c" : "#e0e0e0")};

  &:hover {
    border-color: #00838c;
    box-shadow: 0 2px 8px rgba(0, 131, 140, 0.15);
  }
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TrackingDetails = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  padding: 12px;
  background: #ffebee;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
`;

const NoDataMessage = styled.div`
  color: #666;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  font-size: 14px;
  text-align: center;
  font-family: openSans-SemiBold, sans-serif;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
  font-family: openSans-SemiBold, sans-serif;
`;

// Pagination Styles
const PaginationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e8e8e8;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const PaginationInfo = styled.span`
  font-size: 12px;
  color: #666;
  font-weight: 500;
  font-family: openSans-SemiBold, sans-serif;
`;

const PaginationContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`;

const PaginationButton = styled.button.attrs({ type: "button" })`
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: white;
  color: #00838c;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: openSans-SemiBold, sans-serif;

  &:hover:not(:disabled) {
    background: #00838c;
    color: white;
    border-color: #00838c;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageButton = styled.button.attrs({ type: "button" })`
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  background: ${(props) => (props.active ? "#00838c" : "white")};
  color: ${(props) => (props.active ? "white" : "#00838c")};
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
  font-family: openSans-SemiBold, sans-serif;

  &:hover:not(:disabled) {
    background: #00838c;
    color: white;
    border-color: #00838c;
  }

  &:disabled {
    cursor: default;
    color: #999;
    border-color: #ddd;
    background: white;
  }
`;

export const Container = styled.div`
  padding: 30px 20px;
  min-height: 100vh;
  font-family: openSans-SemiBold, sans-serif;
`;

export const TimelineWrapper = styled.div`
  padding: 0 16px 16px;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 1000px;
    }
  }
`;

export const Timeline = styled.div`
  position: relative;
  padding-left: 0;

  &::before {
    content: "";
    position: absolute;
    left: 7px;
    top: 24px;
    bottom: 0;
    width: 2px;
    background: linear-gradient(to bottom, #00838c, transparent);
  }
`;

export const TimelineEvent = styled.div`
  position: relative;
  margin-bottom: 24px;
  padding-left: 40px;
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.isLast &&
    `
    &::before {
      content: "";
      position: absolute;
      left: 7px;
      top: 24px;
      bottom: 0;
      width: 2px;
      background: transparent;
    }
  `}
`;

export const TimelineDot = styled.div`
  position: absolute;
  left: 0;
  top: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border: 3px solid #00838c;
  border-radius: 50%;
  transition: all 0.3s;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);

  ${TimelineEvent}:hover & {
    background: #00838c;
    box-shadow: 0 0 12px rgba(102, 126, 234, 0.4);
    transform: scale(1.3);
  }
`;

export const EventContent = styled.div`
  background: #f9f9fa;
  padding: 12px;
  border-radius: 8px;
  border-left: 2px solid #00838c;
  transition: all 0.2s;

  ${TimelineEvent}:hover & {
    background: #f0f2f5;
    border-left-color: #764ba2;
  }
`;

export const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
`;

export const EventLocation = styled.h4`
  margin: 0;
  font-size: 13px;
  flex: 1;
  font-weight: 700;
  color: #293461;
  font-family: openSans-SemiBold, sans-serif;
`;

export const EventTime = styled.p`
  margin: 0;
  font-size: 10px;
  color: #8294a5;
  white-space: nowrap;
  font-family: openSans-SemiBold, sans-serif;
`;

export const EventType = styled.p`
  margin: 0;
  font-size: 12px;
  color: #8294a5;
  font-family: openSans-SemiBold, sans-serif;
`;

export const AWBNumber = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #293461;
  word-break: break-all;
`;

export const LastUpdated = styled.div`
  font-size: 11px;
  color: #8294a5;
  margin-top: 4px;
  font-family: openSans-SemiBold, sans-serif;
`;

export const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;
  font-family: openSans-SemiBold, sans-serif;
  background: ${(props) => props.colors.bg};
  color: ${(props) => props.colors.text};
  border: 1.5px solid ${(props) => props.colors.border};
`;

export const StatusIcon = styled.span`
  font-size: 13px;
`;

export const ExpandIcon = styled.div`
  font-size: 16px;
  color: #00838c;
  transition: transform 0.3s;
  flex-shrink: 0;

  ${(props) =>
    props.isExpanded &&
    `
    transform: rotate(180deg);
  `}
`;

export const ShipmentCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
    border-color: #00838c;
  }

  ${(props) =>
    props.isExpanded &&
    `
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15);
    border-color: #00838c;
  `}
`;

export const ShipmentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%);
`;

export const ShipmentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const formatDate = (timestamp) => {
  if (!timestamp) return "";

  // Input format: 14-11-2025 04:35 PM
  const [datePart, timePart, modifier] = timestamp.split(" ");
  const [day, month, year] = datePart.split("-").map(Number);
  let [hours, minutes] = timePart.split(":").map(Number);

  // Convert 12-hour to 24-hour format
  if (modifier === "PM" && hours !== 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  const date = new Date(year, month - 1, day, hours, minutes);

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getStatusColor = (status) => {
  switch (status) {
    case "Delivered":
      return { bg: "#e8f5e9", text: "#2e7d32", border: "#4caf50" };
    case "In Transit":
      return { bg: "#e3f2fd", text: "#1565c0", border: "#2196f3" };
    case "Out for Delivery":
      return { bg: "#fff3e0", text: "#e65100", border: "#ff9800" };
    default:
      return { bg: "#f5f5f5", text: "#424242", border: "#9e9e9e" };
  }
};

export const getStatusIcon = (status) => {
  switch (status) {
    case "Delivered":
      return "";
    case "In Transit":
      return "";
    case "Out for Delivery":
      return "";
    default:
      return "•";
  }
};
